<template>
    <div class="index-news">
        <Container width="landing">
            <div class="index-news__wrapper">
                <h2 class="index-news__title">
                    Последние новости
                </h2>

                <div class="index-news__block">
                    <div class="index-news__slider">
                        <div ref="swiperRef"
                             class="swiper"
                        >
                            <div class="swiper-wrapper">
                                <IndexNewsItem v-for="(article, index) in news"
                                               :key="index"
                                               class="swiper-slide"
                                               :article="article"
                                />
                            </div>
                            <UiSliderNavigation v-if="news.length > 1"
                                                ref="sliderNavigationRef"
                                                class="index-news__slider-navigation"
                                                :is-button-prev-disabled="isButtonPrevDisabled"
                                                :is-button-next-disabled="isButtonNextDisabled"
                                                theme="neutral"
                            />
                        </div>
                    </div>

                    <div class="index-news__information">
                        <div class="index-news__information-title">
                            Полезная информация и статьи по темам
                        </div>

                        <ul class="index-news__information-list">
                            <li>
                                <NuxtLink class="index-news__information-link"
                                          to="/blog"
                                >
                                    Новости
                                    <WBIcon class="index-news__information-icon"
                                            name="chevron-right"
                                    />
                                </NuxtLink>
                            </li>
                            <li>
                                <NuxtLink class="index-news__information-link"
                                          to="/blog"
                                >
                                    Аналитика
                                    <WBIcon class="index-news__information-icon"
                                            name="chevron-right"
                                    />
                                </NuxtLink>
                            </li>
                            <li>
                                <NuxtLink class="index-news__information-link"
                                          to="/blog"
                                >
                                    Продвижение
                                    <WBIcon class="index-news__information-icon"
                                            name="chevron-right"
                                    />
                                </NuxtLink>
                            </li>
                            <li>
                                <NuxtLink class="index-news__information-link"
                                          to="/blog"
                                >
                                    Бизнесу
                                    <WBIcon class="index-news__information-icon"
                                            name="chevron-right"
                                    />
                                </NuxtLink>
                            </li>
                            <li>
                                <NuxtLink class="index-news__information-link"
                                          to="/blog"
                                >
                                    Обновления
                                    <WBIcon class="index-news__information-icon"
                                            name="chevron-right"
                                    />
                                </NuxtLink>
                            </li>
                        </ul>
                    </div>
                </div>

                <slot name="banner" />
            </div>
        </Container>
    </div>
</template>

<script setup lang="ts">
    import type { SwiperOptions } from 'swiper/types'
    import type { NewsItem } from '~/src/shared/components/pages/index/mock/indexNews'
    import type UiSliderNavigation from '~/src/shared/components/ui/slider/UiSliderNavigation.vue'
    import { Navigation, Pagination } from 'swiper/modules'
    import Container from '~/src/shared/components/common/Container/Container.vue'
    import IndexNewsItem from '~/src/shared/components/pages/index/IndexNews/IndexNewsItem.vue'
    import { useSwiper } from '~/src/shared/composables/client/slider/useSwiper'

    defineProps<{
        news: NewsItem[]
    }>()

    const swiperRef = ref<HTMLElement | null>(null)

    const sliderNavigationRef = ref<InstanceType<typeof UiSliderNavigation>>()
    const swiperOptions = computed<SwiperOptions>(() => {
        return {
            modules: [Navigation, Pagination],
            slidesPerView: 1,
            spaceBetween: 12,
            navigation: {
                prevEl: sliderNavigationRef.value?.prevEl,
                nextEl: sliderNavigationRef.value?.nextEl,
            },
        }
    })

    const { isButtonPrevDisabled, isButtonNextDisabled } = useSwiper(swiperRef, swiperOptions)
</script>

<style scoped lang="scss">
    .index-news {
        $this: &;

        &__wrapper {
            width: calc(100% + var(--container-offset) * 2);
            margin-left: calc(-1 * var(--container-offset));
            padding: 80px var(--container-offset);
            border-radius: 72px;
            background-color: var(--white);

            @include respond-to(tablet) {
                padding: 80px var(--container-offset) 40px;
            }

            @include respond-to(mobile) {
                overflow: hidden;
                padding: 24px var(--container-offset) 20px;
                border-radius: 32px;
            }
        }

        &__title {
            @include mulish40b;
        }

        &__block {
            display: flex;
            margin-top: 40px;

            @include respond-to(tablet) {
                flex-flow: column;
            }

            @include respond-to(mobile) {
                margin-top: 24px;
            }
        }

        &__slider {
            position: relative;
            width: calc(100% - 322px);

            @include respond-to(tablet) {
                width: 100%;
            }
        }

        &__slider-navigation {
            position: absolute;
            right: 58px;
            bottom: 42px;
            z-index: 2;

            @include respond-to(mobile) {
                right: 24px;
                bottom: 24px;
            }
        }

        &__information {
            display: flex;
            justify-content: center;
            flex-flow: column;
            flex-shrink: 0;
            width: 322px;
            margin-left: 20px;
            padding: 40px;
            border-radius: 36px;
            background-color: var(--neutral-100);

            @include respond-to(tablet) {
                justify-content: flex-start;
                flex-flow: row;
                width: 100%;
                margin-top: 20px;
                margin-left: 0;
                padding: 40px;
            }

            @include respond-to(mobile) {
                flex-flow: column;
            }
        }

        &__information-title {
            @include mulish20b;

            color: var(--grey-500);

            @include respond-to(tablet) {
                max-width: 292px;
            }

            @include respond-to(mobile) {
                max-width: 100%;
            }
        }

        &__information-list {
            margin-top: 20px;

            @include respond-to(tablet) {
                margin-top: 0;
                margin-left: 40px;
            }

            @include respond-to(mobile) {
                margin-top: 20px;
                margin-left: 0;
            }

            li {
                &:not(:first-child) {
                    margin-top: 16px;
                }
            }
        }

        &__information-link {
            display: flex;
            align-items: center;
            transition: color $default-transition;

            @include mulish20b;

            @include hover {
                color: var(--blue-600);

                #{$this}__information-icon {
                    color: var(--blue-600);
                }
            }
        }

        &__information-icon {
            margin-left: 4px;
            font-size: 20px;
            color: var(--grey-500);
            transition: color $default-transition;
        }
    }
</style>
